import React from 'react';

import { cssMap } from '@compiled/react';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import FloatingToolbarButtonComponent from '@atlaskit/editor-common/FloatingToolbar/Button';
import FloatingToolbarSeparatorComponent from '@atlaskit/editor-common/FloatingToolbar/Separator';
import { EditorToolbarProvider } from '@atlaskit/editor-common/toolbar/context';
import { EditorToolbarUIProvider } from '@atlaskit/editor-common/toolbar/EditorToolbarUIProvider';
import { TOOLBARS } from '@atlaskit/editor-common/toolbar/keys';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarButton,
	FloatingToolbarCustom,
	Icon,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model/toolbar-model-renderer';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import { ToolbarDropdownItemSection } from '@atlaskit/editor-toolbar/toolbar-dropdown-item-section';
import { ToolbarDropdownMenu } from '@atlaskit/editor-toolbar/toolbar-dropdown-menu';
import { ToolbarDropdownMenuProvider } from '@atlaskit/editor-toolbar/toolbar-dropdown-menu-context';
import { ToolbarSection } from '@atlaskit/editor-toolbar/toolbar-section';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { Box } from '@atlaskit/primitives/compiled/box';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';
import { token } from '@atlaskit/tokens';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import type { AIPlugin } from '../../../aiPluginType';
import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../../types/types';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { improveWriting } from '../../prebuilt/config-items/prompts/enhance/enhance';
import { AIToolbarComponents } from '../toolbar';
import type { getSmartLinkDropdownItems } from './smartlink-action-items';
import type { SmartLinkAIAction } from './use-smartlink-ai-actions';

const rovoButtonIconStyles = cssMap({
	imageToolbarIcon: {
		display: 'inline-flex',
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.025'),
	},
});

export const floatingToolbarAIButtonTestID = `ak-editor-ai-selection-toolbar-ai-button`;

export const getAIExperienceButton = ({
	intl,
	rangeBaseGenerate,
	hideTitle,
	aiGlobalOptIn,
	icon,
	title,
	tooltip,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
	inlineEditContentOverride,
	openInRovoChat,
	rovoChatAIFeatureContext,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	hideTitle?: boolean;
	icon?: Icon;
	inlineEditContentOverride?: EditorPluginAIConfigItem['inlineEditContentOverride'];
	intl: IntlShape;
	onClick?: () => void;
	onMount?: () => void;
	openInRovoChat?: boolean;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	rovoChatAIFeatureContext?: Record<string, unknown>;
	title?: MessageDescriptor;
	tooltip?: MessageDescriptor;
	triggeredFor?: NodeType;
}) => {
	let didMouseEnter = false;

	const browser = getBrowserInfo();
	const metaKey = browser.mac ? '⌘' : 'Ctrl';
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return {
		type: 'button',
		isRadioButton: false,
		pulse,
		showTitle: true,
		testId: floatingToolbarAIButtonTestID,
		icon: icon ?? Icon,
		...(!hideTitle && {
			title: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		}),
		ariaLabel: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		tooltipContent: intl.formatMessage(tooltip ?? sharedMessages.aiUniversalShortCallToAction, {
			key: metaKey,
		}),
		onMount,
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			const configItem = openInRovoChat
				? { ...rangeBaseGenerate, openInRovoChat: true, rovoChatAIFeatureContext }
				: inlineEditContentOverride
					? { ...rangeBaseGenerate, inlineEditContentOverride }
					: rangeBaseGenerate;

			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem,
				lastTriggeredFrom: 'floatingToolbar',
				// ED-18194 Added analytics for floating toolbar to determine
				// the % of users who click the button vs shift tab to it
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				triggeredFor,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			if (onClick) {
				onClick();
			}

			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const getImproveWritingAIButton = ({
	intl,
	hideTitle,
	aiGlobalOptIn,
	disabled,
	onMount,
	isInDocumentStreamingEnabled,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	hideTitle?: boolean;
	intl: IntlShape;
	isInDocumentStreamingEnabled?: boolean;
	onMount?: () => void;
}) => {
	let didMouseEnter = false;

	return {
		type: 'button',
		isRadioButton: false,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-improve-writing`,
		ariaLabel: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		icon: MagicWandIcon,
		...(!hideTitle && {
			title: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		}),
		tooltipContent: isInDocumentStreamingEnabled
			? undefined
			: intl.formatMessage(aiMessages.improveWritingToolbarIconTooltip),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: improveWriting(),
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
		disabled,
		onMount,
	} as FloatingToolbarButton<Command>;
};

export type AskRovoFloatingToolbarButtonTarget = 'default' | 'remix-app' | 'image';

export const getAskRovoButtonMessages = (
	target: AskRovoFloatingToolbarButtonTarget = 'default',
): { title: MessageDescriptor; tooltip: MessageDescriptor } => {
	switch (target) {
		case 'remix-app':
			return {
				title: aiMessages.askRovoToolbarRemixAppTitle,
				tooltip: aiMessages.askRovoToolbarRemixAppTooltipQuickCommand,
			};
		case 'image':
			return {
				title: aiMessages.askRovoToolbarImageTitle,
				tooltip: aiMessages.askRovoToolbarImageTooltipQuickCommand,
			};
		case 'default':
		default:
			return {
				title: aiMessages.askRovoToolbarIconTitle,
				tooltip: aiMessages.askRovoToolbarIconTooltipQuickCommand,
			};
	}
};

export const getAskAIButton = ({
	intl,
	rangeBaseGenerate,
	aiGlobalOptIn,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
	inlineEditContentOverride,
	openInRovoChat,
	rovoChatAIFeatureContext,
	target = 'default',
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	inlineEditContentOverride?: EditorPluginAIConfigItem['inlineEditContentOverride'];
	intl: IntlShape;
	onClick?: () => void;
	onMount?: () => void;
	openInRovoChat?: boolean;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	rovoChatAIFeatureContext?: Record<string, unknown>;
	target?: AskRovoFloatingToolbarButtonTarget;
	triggeredFor?: NodeType;
}): FloatingToolbarButton<Command> => {
	const { title, tooltip } = getAskRovoButtonMessages(target);
	const Icon =
		target === 'image'
			? () => (
					<Box as="span" xcss={rovoButtonIconStyles.imageToolbarIcon}>
						<RovoIcon size="xxsmall" />
					</Box>
				)
			: () => <RovoIcon size="xxsmall" />;

	return getAIExperienceButton({
		intl,
		rangeBaseGenerate,
		hideTitle: false,
		aiGlobalOptIn,
		icon: Icon,
		title,
		tooltip,
		triggeredFor,
		pulse,
		onMount,
		onClick,
		disabled,
		inlineEditContentOverride,
		openInRovoChat,
		rovoChatAIFeatureContext,
	});
};

// Renderer component to allow React hooks for memoization
const RovoSplitButtonRenderer = ({
	view,
	api,
	editorPluginAIProvider,
	aiGlobalOptIn,
	onMount,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	onMount?: () => void;
	view: EditorView;
}) => {
	const registeredComponents = api?.toolbar?.actions.getComponents?.();

	const components = React.useMemo(
		() =>
			registeredComponents ??
			AIToolbarComponents({
				editorPluginAIProvider,
				aiGlobalOptIn,
				isNewToolbarEnabled: false,
				api,
			}),
		[registeredComponents, editorPluginAIProvider, aiGlobalOptIn, api],
	);

	const toolbarConfig = React.useMemo(
		() => ({
			type: 'toolbar' as const,
			key: TOOLBARS.INLINE_TEXT_TOOLBAR,
			component: ({ children }: { children: React.ReactNode }) => <>{children}</>,
		}),
		[],
	);

	const fallbacks = React.useMemo(
		() => ({
			group: ToolbarButtonGroup,
			section: ToolbarSection,
			menuSection: ToolbarDropdownItemSection,
		}),
		[],
	);

	React.useEffect(() => {
		onMount?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<EditorToolbarProvider editorView={view ?? null}>
			<EditorToolbarUIProvider api={api}>
				<ToolbarModelRenderer
					toolbar={toolbarConfig}
					components={components}
					fallbacks={fallbacks}
				/>
			</EditorToolbarUIProvider>
		</EditorToolbarProvider>
	);
};

export const getAskRovoAiSplitButton = (props: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	disabled: boolean;
	editorPluginAIProvider: EditorPluginAIProvider;
	intl: IntlShape;
	onMount?: () => void;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	triggeredFor?: NodeType;
}): FloatingToolbarCustom<Command> => {
	const {
		intl,
		rangeBaseGenerate,
		aiGlobalOptIn,
		editorPluginAIProvider,
		disabled,
		api,
		triggeredFor,
		pulse,
		onMount,
	} = props;

	return {
		type: 'custom',
		fallback: [
			getAskAIButton({
				intl,
				rangeBaseGenerate,
				aiGlobalOptIn: props.aiGlobalOptIn,
				triggeredFor,
				pulse,
				onMount,
				disabled,
			}),
		],
		render: (view) => {
			if (!view) {
				return null;
			}

			return (
				<RovoSplitButtonRenderer
					onMount={onMount}
					view={view}
					api={api}
					editorPluginAIProvider={editorPluginAIProvider}
					aiGlobalOptIn={aiGlobalOptIn}
				/>
			);
		},
	};
};

/** Renders the shared Smart Link toolbar controls for both renderer paths. */
export const SmartLinkToolbarControls = ({
	areAnyNewToolbarFlagsEnabled,
	disabled,
	dropdownItems,
	dropdownItemHandlers,
	dropdownEmptyStateLabel,
	dropdownEmptyStateDisabled,
	onRetry,
	forceDropdown = false,
	intl,
	errorTooltipContent,
	onRovoClick,
	onSummarizeClick,
	pendingAction,
	rovoDescriptor,
	summarizeDescriptor,
	view,
}: {
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	dropdownEmptyStateDisabled?: boolean;
	dropdownEmptyStateLabel?: React.ReactNode;
	dropdownItemHandlers?: ReadonlyMap<string, () => void>;
	dropdownItems: ReturnType<typeof getSmartLinkDropdownItems>;
	errorTooltipContent?: React.ReactNode;
	forceDropdown?: boolean;
	intl: IntlShape;
	onRetry?: () => void;
	onRovoClick?: () => void;
	onSummarizeClick?: () => void;
	pendingAction?: SmartLinkAIAction;
	rovoDescriptor: FloatingToolbarButton<Command>;
	summarizeDescriptor: FloatingToolbarButton<Command>;
	view: EditorView;
}): React.JSX.Element => {
	const dispatchCommand = (cmd: Command) => cmd(view.state, view.dispatch, view);
	const shouldShowDropdown = forceDropdown || dropdownItems.length > 0;
	const RovoIcon = rovoDescriptor.icon;
	const SummarizeButtonIcon = summarizeDescriptor.icon;

	return (
		<>
			<FloatingToolbarButtonComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				testId={rovoDescriptor.testId}
				title={rovoDescriptor.title}
				ariaLabel={rovoDescriptor.ariaLabel}
				tooltipContent={errorTooltipContent ?? rovoDescriptor.tooltipContent}
				disabled={rovoDescriptor.disabled}
				icon={RovoIcon ? <RovoIcon label="" /> : undefined}
				onMount={rovoDescriptor.onMount}
				isLoading={pendingAction === 'rovo' ? true : undefined}
				/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
				onClick={onRovoClick ?? (() => dispatchCommand(rovoDescriptor.onClick))}
			>
				{rovoDescriptor.showTitle && rovoDescriptor.title}
			</FloatingToolbarButtonComponent>
			{shouldShowDropdown && (
				<ToolbarDropdownMenuProvider>
					<ToolbarDropdownMenu
						iconBefore={<ChevronDownIcon label="" size="small" />}
						testId={`${floatingToolbarAIButtonTestID}-smartlink-rovo-chevron`}
						isDisabled={disabled}
						label={intl.formatMessage(aiMessages.rovoToolbarDropdownChevronLabel)}
					>
						<ToolbarDropdownItemSection>
							{dropdownItems.length === 0 && dropdownEmptyStateLabel !== undefined && (
								<ToolbarDropdownItem isDisabled={dropdownEmptyStateDisabled} onClick={onRetry}>
									{dropdownEmptyStateLabel}
								</ToolbarDropdownItem>
							)}
							{dropdownItems.map((item) => (
								<ToolbarDropdownItem
									key={item.testId}
									elemBefore={item.icon}
									isDisabled={disabled}
									onClick={dropdownItemHandlers?.get(item.testId) ?? item.onClick}
									testId={item.testId}
								>
									{item.label}
								</ToolbarDropdownItem>
							))}
						</ToolbarDropdownItemSection>
					</ToolbarDropdownMenu>
				</ToolbarDropdownMenuProvider>
			)}
			<FloatingToolbarButtonComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				testId={summarizeDescriptor.testId}
				title={summarizeDescriptor.title}
				ariaLabel={summarizeDescriptor.ariaLabel}
				tooltipContent={errorTooltipContent ?? summarizeDescriptor.tooltipContent}
				disabled={summarizeDescriptor.disabled}
				icon={SummarizeButtonIcon ? <SummarizeButtonIcon label="" /> : undefined}
				onMount={summarizeDescriptor.onMount}
				isLoading={pendingAction === 'summarize' ? true : undefined}
				/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
				onClick={onSummarizeClick ?? (() => dispatchCommand(summarizeDescriptor.onClick))}
			>
				{summarizeDescriptor.showTitle && summarizeDescriptor.title}
			</FloatingToolbarButtonComponent>
			<FloatingToolbarSeparatorComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				fullHeight={editorExperiment('platform_editor_controls', 'variant1')}
			/>
		</>
	);
};
